/**
 * Phase 5 PR 5.2 — macro-plan phase grouping + current-week helpers.
 *
 * Pure helpers. The macro-plan view groups consecutive same-mode
 * weeks into "phases" for the swimlane visual, and computes which
 * week is "now" from `week_start_date` (NOT `season.start_date` — the
 * macro plan generates from TODAY → championships, not from season
 * start, per Phase 5 plan §"Round 2 → 3" guardrail).
 *
 * Pure-Node — no React, no Supabase. Unit-testable in isolation.
 */

import type { MacroPlanWeek } from '../hooks/useMacroPlan';

export interface MacroPhase {
  /** First week_number in this phase (inclusive). */
  startWeek: number;
  /** Last week_number in this phase (inclusive). */
  endWeek: number;
  /** Mode id shared by all weeks in the phase. */
  mode: string;
  /** All weeks belonging to this phase, in week_number order. */
  weeks: MacroPlanWeek[];
  /** True when at least one week in the phase has a meet_id. */
  hasMeet: boolean;
}

/**
 * Group consecutive same-mode weeks into phases. Mirrors the vanilla
 * grouping at `src/renderer/js/seasons/macro-plan.js:251-262`.
 *
 * Input MUST be sorted by week_number ASC (the API already does this).
 */
export function groupPhases(weeks: MacroPlanWeek[]): MacroPhase[] {
  const phases: MacroPhase[] = [];
  let cur: MacroPhase | null = null;
  for (const w of weeks) {
    if (!cur || cur.mode !== w.mode) {
      if (cur) phases.push(cur);
      cur = {
        startWeek: w.week_number,
        endWeek: w.week_number,
        mode: w.mode,
        weeks: [w],
        hasMeet: !!w.meet_id,
      };
    } else {
      cur.endWeek = w.week_number;
      cur.weeks.push(w);
      if (w.meet_id) cur.hasMeet = true;
    }
  }
  if (cur) phases.push(cur);
  return phases;
}

/**
 * Season-model Phase 2 (2b) — the ONE current-week resolver.
 *
 * Explicit states (cross-cutting contract #3) so every surface treats "now"
 * the same way and NOTHING falls back to `MAX(week_number)`:
 *   - `no_plan`       — no macro rows at all → "generate your plan" empty-state
 *   - `invalid`       — rows exist but none carry a usable date → like no_plan
 *   - `before_season` — today precedes the season start → pre-season UI, no week
 *   - `in_season`     — the week whose week_start_date is the latest ≤ today
 *   - `after_season`  — today is past the LAST week's final day (Monday + 6);
 *                       weekNumber clamped to the last week for display (the
 *                       "season complete / extend" affordance is Phase 3)
 *
 * `seasonStart` (the season's own start_date) is the before-season boundary
 * when provided — post-2a it equals the first week's date; we fall back to the
 * first dated week when it's null. `weekNumber` is null for
 * before_season/no_plan/invalid (drives empty-states, NOT a stale week).
 */
export type CurrentWeekState =
  | 'before_season'
  | 'in_season'
  | 'after_season'
  | 'no_plan'
  | 'invalid';

export interface CurrentWeekResolution {
  state: CurrentWeekState;
  weekNumber: number | null;
}

export function resolveCurrentWeek(
  weeks: MacroPlanWeek[] | null,
  seasonStart: string | null = null,
  now: Date = new Date(),
): CurrentWeekResolution {
  if (!weeks || weeks.length === 0) return { state: 'no_plan', weekNumber: null };

  // Keep only rows with a parseable YYYY-MM-DD week_start_date, sorted ASC.
  const dated = weeks
    .filter((w) => !!w.week_start_date && parseLocalIso(w.week_start_date) !== null)
    .sort((a, b) =>
      (a.week_start_date as string).localeCompare(b.week_start_date as string),
    );
  if (dated.length === 0) return { state: 'invalid', weekNumber: null };

  const todayMid = midnightLocal(now).getTime();
  const firstRow = dated[0];
  const lastRow = dated[dated.length - 1];

  // Before-season cutoff: explicit seasonStart when given, else the first week.
  const firstStart =
    parseLocalIso(seasonStart ?? '') ?? parseLocalIso(firstRow.week_start_date as string)!;
  if (todayMid < firstStart.getTime()) return { state: 'before_season', weekNumber: null };

  // Past the last week's final day (its Monday + 6) → season is over.
  const lastEnd = addDaysLocal(parseLocalIso(lastRow.week_start_date as string)!, 6);
  if (todayMid > lastEnd.getTime()) {
    return { state: 'after_season', weekNumber: lastRow.week_number };
  }

  // In season — the week whose start_date is the latest still ≤ today.
  let current: MacroPlanWeek | null = null;
  for (const w of dated) {
    if (parseLocalIso(w.week_start_date as string)!.getTime() <= todayMid) {
      current = w; // latest-wins (sorted ASC)
    } else {
      break;
    }
  }
  // No week has actually started yet (today ≥ seasonStart but < first week,
  // a seasonStart/first-week divergence) → pre-season.
  if (!current) return { state: 'before_season', weekNumber: null };
  return { state: 'in_season', weekNumber: current.week_number };
}

/**
 * Determine the current week from `week_start_date`. Thin wrapper over
 * `resolveCurrentWeek` (single source of truth — NO independent date logic).
 * Returns the `week_number` of the latest week ≤ today (in_season), the last
 * week once the season is over (after_season), or null for
 * before_season/no_plan/invalid. Caller hides the "Now" indicator on null.
 */
export function currentWeekFromDates(
  weeks: MacroPlanWeek[],
  now: Date = new Date(),
): number | null {
  const { state, weekNumber } = resolveCurrentWeek(weeks, null, now);
  return state === 'in_season' || state === 'after_season' ? weekNumber : null;
}

/**
 * Compute the swimlane "Now" marker position as a percentage from
 * left (0–100). Anchors on the current week's index in the sorted
 * weeks array (NOT week_number, which may not start at 1).
 *
 * Returns null when there's no current week (hide the marker).
 */
export function nowMarkerPct(
  weeks: MacroPlanWeek[],
  currentWeek: number | null,
): number | null {
  if (currentWeek === null || weeks.length === 0) return null;
  const idx = weeks.findIndex((w) => w.week_number === currentWeek);
  if (idx === -1) return null;
  // Place the marker at the CENTER of the current week's slice.
  const pct = ((idx + 0.5) / weeks.length) * 100;
  return Math.max(0, Math.min(100, pct));
}

function midnightLocal(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Local-day arithmetic (no timezone drift): same wall-clock day + n.
function addDaysLocal(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

// Format a Date as YYYY-MM-DD in LOCAL time (not UTC).
function toLocalIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ─── Phase 2 — shared date-only helpers (cross-cutting #2, frontend mirror) ───
// Local-day semantics (browser TZ), date-only YYYY-MM-DD. Used by Season 2c's
// "Start your season" flow for provisional season dates (today → +16 weeks),
// which 2a's reconciliation later overwrites with the real macro span.

/** Today as YYYY-MM-DD in the user's local timezone. */
export function localTodayIso(now: Date = new Date()): string {
  return toLocalIso(now);
}

/** startIso + N weeks → YYYY-MM-DD (local, date-only). Returns input on bad ISO. */
export function addWeeksIso(startIso: string, weeks: number): string {
  const d = parseLocalIso(startIso);
  if (!d) return startIso;
  return toLocalIso(addDaysLocal(d, weeks * 7));
}

/**
 * SP-1 — the Monday on or before an ISO date (a Monday returns itself). Mirrors
 * the backend `mondayOnOrBefore` (api/lib/season-week.js) so the provisional
 * "Start your season" dates snap the same way the macro generator anchors week 1
 * (2a reconciliation later overwrites them; this keeps the pre-reconcile UI
 * consistent). Returns input on a malformed ISO.
 */
export function mondayOnOrBeforeIso(startIso: string): string {
  const d = parseLocalIso(startIso);
  if (!d) return startIso;
  const dow = (d.getDay() + 6) % 7; // 0=Mon..6=Sun (local)
  return toLocalIso(addDaysLocal(d, -dow));
}

/**
 * Whole days from `now` (local midnight) to `targetIso` (local midnight).
 * Negative when the target is in the past. Date-only, local-day semantics —
 * the SAME boundary `resolveCurrentWeek` uses, so a before_season countdown
 * agrees with the resolver's flip to in_season at the Week-1 day edge.
 * Returns 0 on bad ISO.
 */
export function daysUntilIso(targetIso: string, now: Date = new Date()): number {
  const target = parseLocalIso(targetIso);
  if (!target) return 0;
  const ms = midnightLocal(target).getTime() - midnightLocal(now).getTime();
  return Math.round(ms / 86_400_000);
}

function parseLocalIso(iso: string): Date | null {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/**
 * Format a `week_start_date` ISO string ("2026-05-04") as a friendly
 * label ("May 4"). Returns null on missing / malformed input — caller
 * renders "Week N" without a date.
 */
export function formatWeekDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = parseLocalIso(iso);
  if (!d) return null;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d);
}

// ─── Phase 3 (P3-4) — macro-regen drift hash (frontend MIRROR) ───
// EXACT mirror of api/lib/season-week.js `macroWeekDriftHash` (FNV-1a over the
// normalized {mode | week_start | focus | meet_id} canonical serialization).
// The backend stamps training_plans.drafted_for_macro_hash at draft time; this
// recomputes the CURRENT macro row's token at read time so the UI can flag
// "this week's plan changed since you drafted it." Both sides are pinned to the
// SAME expected hex in their unit tests so they can never silently diverge.
function fnv1a32(str: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

export function macroWeekDriftHash(
  row: Pick<MacroPlanWeek, 'mode' | 'week_start_date' | 'focus' | 'meet_id'> | null | undefined,
): string | null {
  if (!row) return null;
  const mode = String(row.mode == null ? '' : row.mode).trim().toLowerCase();
  const weekStart = String(row.week_start_date == null ? '' : row.week_start_date).slice(0, 10);
  const focus = String(row.focus == null ? '' : row.focus).trim();
  const meet = String(row.meet_id == null ? '' : row.meet_id).trim();
  return fnv1a32([mode, weekStart, focus, meet].join('~|~'));
}
